import React, { useRef, useEffect, useState } from 'react';
import './RichTextEditor.css';

/**
 * RichTextEditor Component (Enhanced)
 * 
 * A comprehensive rich text editor using contentEditable for CMS content editing.
 * Provides full formatting capabilities including:
 * - Text formatting: Bold, Italic, Underline, Strikethrough
 * - Headings: H1, H2, H3, Paragraph
 * - Lists: Ordered, Unordered
 * - Alignment: Left, Center, Right, Justify
 * - Links: Insert/Edit/Remove
 * - Blockquote
 * - Inline code
 * - Undo/Redo
 * - Clear formatting
 */
const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Enter content...',
  minHeight = '150px',
  onInsertImage = null // Optional callback for image insertion
}) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);

  // Initialize content only once
  useEffect(() => {
    if (editorRef.current && !isInitialized.current && value) {
      editorRef.current.innerHTML = value;
      isInitialized.current = true;
    }
  }, [value]);

  // Update content when value changes externally
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      // Only update if the editor doesn't have focus
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleLink = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    // Check if we're already in a link
    const parentLink = selection.anchorNode?.parentElement?.closest('a');
    const currentUrl = parentLink?.href || '';
    
    const url = prompt('Enter URL:', currentUrl || 'https://');
    if (url) {
      if (url === '' || url === 'https://') {
        // Remove link
        execCommand('unlink');
      } else {
        execCommand('createLink', url);
      }
    }
  };

  const handleRemoveLink = () => {
    execCommand('unlink');
  };

  const handleHeading = (tag) => {
    execCommand('formatBlock', tag);
    setShowHeadingDropdown(false);
  };

  const handleBlockquote = () => {
    execCommand('formatBlock', 'blockquote');
  };

  const handleCode = () => {
    // Wrap selection in <code> tag
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      
      if (selectedText) {
        const code = document.createElement('code');
        code.textContent = selectedText;
        range.deleteContents();
        range.insertNode(code);
        handleInput();
      }
    }
  };

  const handleIndent = () => {
    execCommand('indent');
  };

  const handleOutdent = () => {
    execCommand('outdent');
  };

  const handleImageInsert = () => {
    if (onInsertImage) {
      onInsertImage();
    } else {
      const url = prompt('Enter image URL:');
      if (url) {
        execCommand('insertImage', url);
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.rte-heading-dropdown')) {
        setShowHeadingDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="rich-text-editor-wrapper" style={{ '--min-height': minHeight }}>
      {/* Toolbar */}
      <div className="rte-toolbar">
        {/* Undo/Redo */}
        <button 
          type="button" 
          onClick={() => execCommand('undo')} 
          className="rte-btn"
          title="Undo (Ctrl+Z)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7v6h6M3 13c0-4.97 4.03-9 9-9s9 4.03 9 9-4.03 9-9 9a9 9 0 01-7.5-4" />
          </svg>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('redo')} 
          className="rte-btn"
          title="Redo (Ctrl+Y)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 7v6h-6M21 13c0-4.97-4.03-9-9-9s-9 4.03-9 9 4.03 9 9 9a9 9 0 007.5-4" />
          </svg>
        </button>
        
        <span className="rte-separator" />
        
        {/* Headings Dropdown */}
        <div className="rte-heading-dropdown">
          <button 
            type="button" 
            onClick={(e) => {
              e.stopPropagation();
              setShowHeadingDropdown(!showHeadingDropdown);
            }}
            className="rte-btn rte-dropdown-btn"
            title="Heading"
          >
            H ▾
          </button>
          {showHeadingDropdown && (
            <div className="rte-dropdown-menu">
              <button type="button" onClick={() => handleHeading('p')}>Paragraph</button>
              <button type="button" onClick={() => handleHeading('h1')}>Heading 1</button>
              <button type="button" onClick={() => handleHeading('h2')}>Heading 2</button>
              <button type="button" onClick={() => handleHeading('h3')}>Heading 3</button>
              <button type="button" onClick={() => handleHeading('h4')}>Heading 4</button>
            </div>
          )}
        </div>
        
        <span className="rte-separator" />
        
        {/* Text Formatting */}
        <button 
          type="button" 
          onClick={() => execCommand('bold')} 
          className="rte-btn"
          title="Bold (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('italic')} 
          className="rte-btn"
          title="Italic (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('underline')} 
          className="rte-btn"
          title="Underline (Ctrl+U)"
        >
          <u>U</u>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('strikeThrough')} 
          className="rte-btn"
          title="Strikethrough"
        >
          <s>S</s>
        </button>
        
        <span className="rte-separator" />
        
        {/* Alignment */}
        <button 
          type="button" 
          onClick={() => execCommand('justifyLeft')} 
          className="rte-btn"
          title="Align Left"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('justifyCenter')} 
          className="rte-btn"
          title="Align Center"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('justifyRight')} 
          className="rte-btn"
          title="Align Right"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('justifyFull')} 
          className="rte-btn"
          title="Justify"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        
        <span className="rte-separator" />
        
        {/* Lists */}
        <button 
          type="button" 
          onClick={() => execCommand('insertUnorderedList')} 
          className="rte-btn"
          title="Bullet List"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="9" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="9" y1="18" x2="21" y2="18"/>
            <circle cx="4" cy="6" r="2" fill="currentColor"/><circle cx="4" cy="12" r="2" fill="currentColor"/><circle cx="4" cy="18" r="2" fill="currentColor"/>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('insertOrderedList')} 
          className="rte-btn"
          title="Numbered List"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
            <text x="3" y="8" fontSize="8" fill="currentColor">1</text>
            <text x="3" y="14" fontSize="8" fill="currentColor">2</text>
            <text x="3" y="20" fontSize="8" fill="currentColor">3</text>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={handleOutdent} 
          className="rte-btn"
          title="Decrease Indent"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            <polyline points="6 9 3 12 6 15"/>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={handleIndent} 
          className="rte-btn"
          title="Increase Indent"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            <polyline points="3 9 6 12 3 15"/>
          </svg>
        </button>
        
        <span className="rte-separator" />
        
        {/* Block Elements */}
        <button 
          type="button" 
          onClick={handleBlockquote} 
          className="rte-btn"
          title="Blockquote"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21z"/>
            <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3z"/>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={handleCode} 
          className="rte-btn"
          title="Inline Code"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
          </svg>
        </button>
        
        <span className="rte-separator" />
        
        {/* Links */}
        <button 
          type="button" 
          onClick={handleLink} 
          className="rte-btn"
          title="Insert/Edit Link"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        </button>
        <button 
          type="button" 
          onClick={handleRemoveLink} 
          className="rte-btn"
          title="Remove Link"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </button>
        
        {/* Image Insert (if callback provided) */}
        {onInsertImage && (
          <>
            <span className="rte-separator" />
            <button 
              type="button" 
              onClick={handleImageInsert} 
              className="rte-btn"
              title="Insert Image"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </button>
          </>
        )}
        
        <span className="rte-separator" />
        
        {/* Clear Formatting */}
        <button 
          type="button" 
          onClick={() => execCommand('removeFormat')} 
          className="rte-btn"
          title="Clear Formatting"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      
      {/* Editor Area */}
      <div
        ref={editorRef}
        className="rte-editor"
        contentEditable
        onInput={handleInput}
        data-placeholder={placeholder}
        style={{ minHeight }}
      />
    </div>
  );
};

export default RichTextEditor;
